import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Header } from './Header';
import { useWorkspace } from '../hooks/useWorkspace';
import type { Doc } from '../../../../backend/convex/_generated/dataModel';
import { Unauthorized } from '../pages/Unauthorized';

type WorkspaceWithOrg = Doc<"workspaces"> & { 
  role: string | null;
  organization?: Doc<"organizations"> | null;
};

interface WorkspaceAdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

export const WorkspaceAdminLayout: React.FC<WorkspaceAdminLayoutProps> = ({ children, title }) => {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { workspace, isLoading, isModerator } = useWorkspace();

  if (isLoading) {
    return (
      <div className="app-container">
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          Loading workspace...
        </div>
      </div>
    );
  }

  if (!workspace || !isModerator) {
    return <Unauthorized message="You do not have administrative access to this workspace." />;
  }

  const navItems = [
    { label: 'Assessments', path: `/w/${workspaceSlug}/admin/assessments`, requiredFeature: 'assessments' },
    { label: 'Assignments', path: `/w/${workspaceSlug}/admin/assignments`, requiredFeature: 'assignments' },
    { label: 'Monitoring', path: `/w/${workspaceSlug}/admin/monitoring`, requiredFeature: 'assessments' },
    { label: 'Problems', path: `/w/${workspaceSlug}/admin/problems` },
    { label: 'Members', path: `/w/${workspaceSlug}/admin/members` },
    { label: 'Settings', path: `/w/${workspaceSlug}/admin/settings`, adminOnly: true },
  ];

  // Add Billing link if workspace belongs to an organization
  const workspaceWithOrg = workspace as WorkspaceWithOrg;
  if (workspaceWithOrg.organization) {
    navItems.push({ 
      label: 'Billing', 
      path: `/org/${workspaceWithOrg.organization.slug}/admin/billing`,
      adminOnly: true 
    });
  }

  return (
    <div className="app-container flex flex-col h-screen bg-background">
      <Header>
        <div className="ml-4 pl-4 border-l border-border text-muted-foreground font-medium hidden sm:block">
          {workspace.name} <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded ml-1 uppercase tracking-wider">Admin</span>
        </div>
      </Header>
      
      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 border-r border-border bg-card hidden md:flex flex-col py-6">
          <nav className="flex-1">
            <ul className="space-y-1 px-3">
              {navItems.map((item) => {
                // Check RBAC
                if (item.adminOnly && workspace.role !== 'admin') return null;
                
                // Check Feature Flags
                if (item.requiredFeature && workspace.features && !workspace.features[item.requiredFeature as keyof typeof workspace.features]) {
                  return null;
                }

                return (
                  <li key={item.path}>
                    <NavLink 
                      to={item.path}
                      className={({ isActive }) => `
                        flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all
                        ${isActive 
                          ? 'bg-primary text-primary-foreground shadow-sm' 
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                      `}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          <div className="px-6 pt-6 border-t border-border mt-auto">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {workspace.name[0].toUpperCase()}
              </div>
              <div className="flex flex-col overflow-hidden">
                <span className="text-xs font-bold truncate">{workspace.name}</span>
                <span className="text-[10px] text-muted-foreground capitalize">{workspace.role}</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto bg-muted/20">
          <div className="max-w-6xl mx-auto p-8 lg:p-12">
            <header className="mb-10">
              <h2 className="text-3xl font-extrabold tracking-tight">{title}</h2>
              <div className="h-1 w-12 bg-primary mt-2 rounded-full"></div>
            </header>
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};


